// =============================================================================
// Google Maps loader. Lazily injects the Places script with the runtime API
// key fetched from /api/meta/config. Multiple consumers can call
// `loadGoogleMaps()` and they'll share the same in-flight promise.
// =============================================================================

let scriptPromise: Promise<typeof google> | null = null;

export function loadGoogleMaps(apiKey: string): Promise<typeof google> {
  if (scriptPromise) return scriptPromise;
  if (!apiKey) {
    return Promise.reject(new Error("Maps API key is missing — set MAPS_API_KEY in the API."));
  }

  scriptPromise = new Promise((resolvePromise, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Google Maps cannot load outside the browser"));
      return;
    }

    if (window.google?.maps?.places) {
      resolvePromise(window.google);
      return;
    }

    const callbackName = `__mtform_gm_cb_${Math.random().toString(36).slice(2)}`;
    (window as unknown as Record<string, unknown>)[callbackName] = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      if (window.google?.maps?.places) resolvePromise(window.google);
      else reject(new Error("Google Maps loaded but Places library is missing"));
    };

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&libraries=places&loading=async&callback=${callbackName}`;
    script.onerror = () => reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export function googleMapsUnavailable(): boolean {
  return scriptPromise === null;
}
